import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Pencil, Plus, Printer, RotateCcw } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { UniformPrinterSummaryType } from '@aeris/contracts'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import {
  createUniformPrinter,
  fetchUniformPrinters,
  updateUniformPrinter,
} from '../lib/api/uniforms.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * As impressoras da produção — F170, no molde de Marcas (F158).
 *
 * A 4Play tem cinco máquinas — Amarela 1, Amarela 2, Branca 1, Branca 2, Preta —
 * e cada uma tira a cor um pouco diferente. Quando o cliente pede para repetir
 * um pedido, a pergunta é "em qual saiu da outra vez?", e a resposta só existe
 * se a fila marcou a máquina. Este cadastro é a lista de onde a fila escolhe.
 *
 * Renomear é seguro (o pedido guarda a máquina, não o nome); arquivar tira do
 * seletor e mantém o nome nos pedidos antigos.
 */

const PrintersPanel = (): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canManage = allows('uniforms.manage')

  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<UniformPrinterSummaryType | null>(null)
  const [newName, setNewName] = useState('')

  const query = useQuery({
    queryKey: ['uniform-printers', 'all'],
    queryFn: async ({ signal }) =>
      await fetchUniformPrinters(undefined, signal),
  })

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['uniform-printers'] })
  }

  const failed = (fallback: string) => (cause: unknown) => {
    setError(cause instanceof ApiError ? cause.message : fallback)
  }

  const create = useMutation({
    mutationFn: async () => await createUniformPrinter({ name }),
    onSuccess: async () => {
      setName('')
      setError(null)
      await invalidate()
    },
    onError: failed('Não foi possível cadastrar a impressora.'),
  })

  const changeStatus = useMutation({
    mutationFn: async (printer: UniformPrinterSummaryType) =>
      await updateUniformPrinter(printer.id, {
        status: printer.status === 'active' ? 'archived' : 'active',
        version: printer.version,
      }),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: failed('Não foi possível alterar a impressora.'),
  })

  const rename = useMutation({
    mutationFn: async (printer: UniformPrinterSummaryType) =>
      await updateUniformPrinter(printer.id, {
        name: newName,
        version: printer.version,
      }),
    onSuccess: async () => {
      setError(null)
      setEditing(null)
      await invalidate()
    },
    onError: failed('Não foi possível renomear a impressora.'),
  })

  const printers = query.data ?? []

  return (
    <div className="flex flex-col gap-4">
      {canManage ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim() === '') return
            create.mutate()
          }}
        >
          <label className="flex min-w-48 flex-1 flex-col gap-1">
            <span className="text-(length:--text-xs) text-(--text-muted)">
              Nome
            </span>
            <Input
              value={name}
              maxLength={60}
              placeholder="Branca 2"
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={name.trim() === '' || create.isPending}
            leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
          >
            Cadastrar
          </Button>
        </form>
      ) : null}

      {error === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}

      {query.isPending ? <Spinner label="Carregando impressoras" /> : null}

      {query.isError ? (
        <EmptyState
          icon={<Printer aria-hidden="true" className="h-6 w-6" />}
          title="Não foi possível carregar as impressoras"
          description="Verifique a conexão e tente de novo."
          action={
            <Button
              variant="secondary"
              onClick={() => {
                void query.refetch()
              }}
            >
              Tentar de novo
            </Button>
          }
        />
      ) : null}

      {!query.isPending && !query.isError && printers.length === 0 ? (
        <EmptyState
          icon={<Printer aria-hidden="true" className="h-6 w-6" />}
          title="Nenhuma impressora ainda"
          description={
            canManage
              ? 'Cadastre as máquinas da produção acima. A fila pede a impressora quando o pedido vai para "Imprimindo".'
              : 'Quem administra a loja ainda não cadastrou nenhuma.'
          }
        />
      ) : null}

      {printers.length === 0 || query.isError ? null : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Impressora</TableHeaderCell>
                <TableHeaderCell>Pedidos</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                {canManage ? (
                  <TableHeaderCell className="text-right">
                    Ações
                  </TableHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {printers.map((printer) => (
                <TableRow key={printer.id}>
                  <TableCell>{printer.name}</TableCell>
                  <TableCell>
                    {printer.usageCount === 0
                      ? '—'
                      : `${String(printer.usageCount)} pedido${printer.usageCount === 1 ? '' : 's'}`}
                  </TableCell>
                  <TableCell>
                    {printer.status === 'active' ? (
                      'Ativa'
                    ) : (
                      <span className="text-(--text-muted)">Arquivada</span>
                    )}
                  </TableCell>
                  {canManage ? (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={
                          <Pencil aria-hidden="true" className="h-4 w-4" />
                        }
                        onClick={() => {
                          setNewName(printer.name)
                          setEditing(printer)
                        }}
                      >
                        Renomear
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={changeStatus.isPending}
                        onClick={() => {
                          changeStatus.mutate(printer)
                        }}
                        leadingIcon={
                          printer.status === 'active' ? (
                            <Archive aria-hidden="true" className="h-4 w-4" />
                          ) : (
                            <RotateCcw aria-hidden="true" className="h-4 w-4" />
                          )
                        }
                      >
                        {printer.status === 'active' ? 'Arquivar' : 'Reativar'}
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing === null ? null : (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null)
          }}
        >
          <DialogContent
            title={`Renomear "${editing.name}"`}
            description="Os pedidos que já saíram nela passam a mostrar o nome novo."
          >
            <form
              id="renomear-impressora"
              onSubmit={(event) => {
                event.preventDefault()
                if (newName.trim() === '') return
                rename.mutate(editing)
              }}
            >
              <Field label="Nome">
                {(field) => (
                  <Input
                    {...field}
                    value={newName}
                    maxLength={60}
                    onChange={(event) => {
                      setNewName(event.target.value)
                    }}
                  />
                )}
              </Field>
            </form>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(null)
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                form="renomear-impressora"
                variant="primary"
                disabled={rename.isPending || newName.trim() === ''}
              >
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export const ImpressorasPage = (): ReactNode => (
  <>
    <TabHeader
      title="Impressoras da produção"
      description={
        <>
          As máquinas em que a produção imprime. A fila pede a impressora quando
          o pedido vai para <strong>Imprimindo</strong>, e é por ela que se sabe
          onde repetir um pedido. Arquivar tira do seletor{' '}
          <strong>sem apagar</strong> o nome dos pedidos antigos.
        </>
      }
    />

    <PrintersPanel />
  </>
)
